import React, { useState, useEffect, useRef } from 'react';

// Add ripple animation
const rippleStyle = document.createElement('style');
rippleStyle.textContent = `
  @keyframes ripple-expand {
    0% {
      transform: translate(-50%, -50%) scale(0.9);
      opacity: 0.7;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.55);
      opacity: 0;
    }
  }
`;
if (typeof document !== 'undefined' && !document.head.querySelector('style[data-ripple]')) {
  rippleStyle.setAttribute('data-ripple', 'true');
  document.head.appendChild(rippleStyle);
}

interface CannonFocusOverlayProps {
  isActive: boolean;
  targetName: string | null;
  targetHp: number;
  targetMaxHp: number;
  targetFaction?: string;
  targetLevel?: number;
  playerHp: number;
  playerMaxHp: number;
  playerSP: number;
  playerMaxSP: number;
  playerLevel?: number;
  onBonusUpdate: (multiplier: number, defenseModifier: number) => void;
  onDeactivate: () => void;
}

const AREA_SIZE = 180;
const TARGET_SIZE = 40;
const CROSSHAIR_SIZE = 20;
const TARGET_SPEED = 0.005;
const CROSSHAIR_SPEED = 0.0075;
const FOCUS_GAIN_RATE = 1.5;
const FOCUS_DECAY_RATE = 1.0;
const UPDATE_THROTTLE_MS = 50;

const AREA_Y_OFFSET = -28;
const PANEL_EXTRA_HEIGHT = 220;

const FIRE_SIZE = 28;
const FIRE_SPAWN_INTERVAL = 2500;
const FIRE_LIFETIME_MIN = 3000;
const FIRE_LIFETIME_MAX = 5000;
const MAX_ACTIVE_FIRES = 3;
const DEFENSE_GAIN_PER_EXTINGUISH = 0.1;
const DEFENSE_LOSS_PER_MISS = 0.15;
const FIRE_PRESSES_REQUIRED = 3;

const FIRE_LETTERS: string[] = ['A', 'B', 'C', 'E', 'F', 'G', 'H', 'J', 'K', 'M', 'N', 'P', 'R', 'T', 'U', 'V', 'X', 'Y'];

const getTargetColorByFaction = (faction: string): string => {
  switch (faction) {
    case 'navy':
      return '#3498db'; // Blue
    case 'pirate':
      return '#e74c3c'; // Red
    case 'merchant':
      return '#f1c40f'; // Gold
    case 'bandit':
      return '#9b59b6'; // Purple
    default:
      return '#da3633'; // Default red
  }
};

interface FireSprite {
  id: number;
  x: number;
  y: number;
  spawnTime: number;
  lifetime: number;
  letter: string;
  pressesRemaining: number;
}

interface CannonFocusState {
  isActive: boolean;
  focusMeter: number;
  targetX: number;
  targetY: number;
  targetDirX: number;
  targetDirY: number;
  crosshairX: number;
  crosshairY: number;
  bonusMultiplier: number;
  defenseModifier: number;
  firesExtinguished: number;
  firesMissed: number;
}

export const CannonFocusOverlay: React.FC<CannonFocusOverlayProps> = ({
  isActive,
  targetName,
  targetHp,
  targetMaxHp,
  targetFaction = 'neutral',
  targetLevel = 1,
  playerHp,
  playerMaxHp,
  playerSP,
  playerMaxSP,
  playerLevel = 1,
  onBonusUpdate,
  onDeactivate,
}): React.ReactElement | null => {
  const [state, setState] = useState<CannonFocusState>({
    isActive: false,
    focusMeter: 0,
    targetX: 0.5,
    targetY: 0.5,
    targetDirX: 1,
    targetDirY: 1,
    crosshairX: 0.5,
    crosshairY: 0.5,
    bonusMultiplier: 1.0,
    defenseModifier: 1.0,
    firesExtinguished: 0,
    firesMissed: 0,
  });

  const [fires, setFires] = useState<FireSprite[]>([]);
  const firesRef = useRef<FireSprite[]>([]);
  const fireIdCounterRef = useRef<number>(0);
  const lastFireSpawnTimeRef = useRef<number>(0);
  const keysRef = useRef<{ [key: string]: boolean }>({});
  const lastTimeRef = useRef<number>(Date.now());
  const lastUpdateTimeRef = useRef<number>(0);
  const frameRef = useRef<number | null>(null);

  const panelWidth = AREA_SIZE + 50;
  const panelHeight = AREA_SIZE + PANEL_EXTRA_HEIGHT;

  // Initialize minigame
  useEffect(() => {
    if (!isActive) return;

    lastTimeRef.current = Date.now();

    setState({
      isActive: true,
      focusMeter: 0,
      targetX: 0.5,
      targetY: 0.5,
      targetDirX: Math.random() > 0.5 ? 1 : -1,
      targetDirY: Math.random() > 0.5 ? 1 : -1,
      crosshairX: 0.5,
      crosshairY: 0.5,
      bonusMultiplier: 1.0,
      defenseModifier: 1.0,
      firesExtinguished: 0,
      firesMissed: 0,
    });

    firesRef.current = [];
    fireIdCounterRef.current = 0;
    lastFireSpawnTimeRef.current = Date.now();
    lastTimeRef.current = Date.now();
  }, [isActive]);

  // Setup keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toUpperCase();
      keysRef.current[key] = true;

      // Handle letter keys for fire suppression
      if (FIRE_LETTERS.includes(key) && firesRef.current.length > 0) {
        e.preventDefault();
        e.stopPropagation();

        const updatedFires = firesRef.current.map((fire) => {
          if (fire.letter === key && fire.pressesRemaining > 0) {
            return { ...fire, pressesRemaining: fire.pressesRemaining - 1 };
          }
          return fire;
        });

        // Remove extinguished fires
        const remainingFires = updatedFires.filter((fire) => {
          if (fire.pressesRemaining <= 0) {
            setState((prev) => ({
              ...prev,
              defenseModifier: Math.min(1.5, prev.defenseModifier + DEFENSE_GAIN_PER_EXTINGUISH),
              firesExtinguished: prev.firesExtinguished + 1,
            }));
            return false;
          }
          return true;
        });

        firesRef.current = remainingFires;
        setFires([...remainingFires]);
      }

      // ESC to close
      if (key === 'ESCAPE') {
        e.preventDefault();
        e.stopPropagation();
        onDeactivate();
      }

      // WASD keys for aiming
      if (['W', 'A', 'S', 'D', 'ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT'].includes(key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toUpperCase();
      keysRef.current[key] = false;

      // Block propagation for all minigame keys
      if (['W', 'A', 'S', 'D', 'ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'ESCAPE'].includes(key) || FIRE_LETTERS.includes(key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [onDeactivate]);

  // Game loop
  useEffect(() => {
    if (!isActive) return;

    const gameLoop = () => {
      const now = Date.now();
      const delta = (now - lastTimeRef.current) / 16.6667; // normalize to 60fps steps
      lastTimeRef.current = now;

      setState((prevState) => {
        let newState = { ...prevState };

        // Update target position with constant velocity based on direction
        const margin = 0.05;
        const minPos = margin;
        const maxPos = 1 - margin;

        // Calculate target speed based on NPC strength relative to player
        // Use stable references to avoid dependency issues
        const strengthRatio = (targetLevel || 1) / Math.max(1, playerLevel || 1);
        const speedMultiplier = Math.min(1.5, 1.0 + (strengthRatio - 1) * 0.5);
        const adjustedTargetSpeed = TARGET_SPEED * speedMultiplier;

        // Calculate new position using direction with strength-based speed
        let newX = newState.targetX + (adjustedTargetSpeed * newState.targetDirX * delta);
        let newY = newState.targetY + (adjustedTargetSpeed * 0.7 * newState.targetDirY * delta);

        // Bounce off boundaries - reverse direction only
        if (newX <= minPos) {
          newX = minPos;
          newState.targetDirX = 1;
        } else if (newX >= maxPos) {
          newX = maxPos;
          newState.targetDirX = -1;
        }

        if (newY <= minPos) {
          newY = minPos;
          newState.targetDirY = 1;
        } else if (newY >= maxPos) {
          newY = maxPos;
          newState.targetDirY = -1;
        }

        newState.targetX = newX;
        newState.targetY = newY;

        // Update crosshair
        const moveUp = keysRef.current['W'] || keysRef.current['ARROWUP'];
        const moveDown = keysRef.current['S'] || keysRef.current['ARROWDOWN'];
        const moveLeft = keysRef.current['A'] || keysRef.current['ARROWLEFT'];
        const moveRight = keysRef.current['D'] || keysRef.current['ARROWRIGHT'];

        if (moveUp) newState.crosshairY -= CROSSHAIR_SPEED;
        if (moveDown) newState.crosshairY += CROSSHAIR_SPEED;
        if (moveLeft) newState.crosshairX -= CROSSHAIR_SPEED;
        if (moveRight) newState.crosshairX += CROSSHAIR_SPEED;

        const crosshairMargin = 0.05;
        newState.crosshairX = Math.max(crosshairMargin, Math.min(1 - crosshairMargin, newState.crosshairX));
        newState.crosshairY = Math.max(crosshairMargin, Math.min(1 - crosshairMargin, newState.crosshairY));

        // Update focus meter
        const dx = newState.crosshairX - newState.targetX;
        const dy = newState.crosshairY - newState.targetY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const threshold = (TARGET_SIZE + CROSSHAIR_SIZE) / (2 * AREA_SIZE);

        if (distance < threshold) {
          const closeness = 1 - (distance / threshold);
          newState.focusMeter = Math.min(100, newState.focusMeter + FOCUS_GAIN_RATE * (1 + closeness));
        } else {
          newState.focusMeter = Math.max(0, newState.focusMeter - FOCUS_DECAY_RATE);
        }

        // Update multiplier
        newState.bonusMultiplier = 1.0 + (newState.focusMeter / 100) * 1.5;

        // Throttle parent updates to prevent frantic changes
        const currentTime = Date.now();
        if (currentTime - lastUpdateTimeRef.current >= UPDATE_THROTTLE_MS) {
          onBonusUpdate(newState.bonusMultiplier, newState.defenseModifier);
          lastUpdateTimeRef.current = currentTime;
        }

        return newState;
      });

      // Spawn fires
      const fireNow = Date.now();
      if (firesRef.current.length < MAX_ACTIVE_FIRES && fireNow - lastFireSpawnTimeRef.current >= FIRE_SPAWN_INTERVAL) {
        const newFire: FireSprite = {
          id: fireIdCounterRef.current++,
          x: 0.15 + Math.random() * 0.7,
          y: 0.15 + Math.random() * 0.7,
          spawnTime: fireNow,
          lifetime: FIRE_LIFETIME_MIN + Math.random() * (FIRE_LIFETIME_MAX - FIRE_LIFETIME_MIN),
          letter: FIRE_LETTERS[Math.floor(Math.random() * FIRE_LETTERS.length)],
          pressesRemaining: FIRE_PRESSES_REQUIRED,
        };

        firesRef.current.push(newFire);
        setFires([...firesRef.current]);
        lastFireSpawnTimeRef.current = fireNow;
      }

      // Update fires
      const updatedFires = firesRef.current.filter((fire) => {
        const age = fireNow - fire.spawnTime;
        if (age > fire.lifetime) {
          setState((prev) => ({
            ...prev,
            defenseModifier: Math.max(0.5, prev.defenseModifier - DEFENSE_LOSS_PER_MISS),
            firesMissed: prev.firesMissed + 1,
          }));
          return false;
        }
        return true;
      });

      if (updatedFires.length !== firesRef.current.length) {
        firesRef.current = updatedFires;
        setFires([...updatedFires]);
      }

      frameRef.current = requestAnimationFrame(gameLoop);
    };

    frameRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [isActive, onBonusUpdate, playerLevel, targetLevel]);

  if (!isActive) return null;

  const targetX = (state.targetX - 0.5) * AREA_SIZE;
  const targetY = (state.targetY - 0.5) * AREA_SIZE + AREA_Y_OFFSET;
  const crossX = (state.crosshairX - 0.5) * AREA_SIZE;
  const crossY = (state.crosshairY - 0.5) * AREA_SIZE + AREA_Y_OFFSET;
  const targetColor = getTargetColorByFaction(targetFaction);

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: panelWidth,
        height: panelHeight,
        background: 'rgba(10, 10, 10, 0.95)',
        border: '2px solid #3a3a3a',
        zIndex: 500,
        fontFamily: '"Seurat", "Sometype Mono", monospace',
        color: '#e2e8f0',
      }}
    >
      {/* Header - NPC Tooltip Style */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '2px solid #30363d',
          background: '#161b22',
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-start',
        }}
      >
        {/* Portrait placeholder */}
        <div
          style={{
            width: 32,
            height: 32,
            background: '#1a1a1a',
            border: '2px solid #3a3a3a',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            color: '#888',
          }}
        >
          ⚔
        </div>

        {/* Target info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: targetColor, fontSize: '11px', fontWeight: 700, marginBottom: '2px' }}>
            {targetName || 'NO TARGET'}
          </div>
          <div style={{ color: '#888', fontSize: '9px', marginBottom: '3px' }}>
            {targetFaction ? targetFaction.toUpperCase() : 'NEUTRAL'} | LVL {targetLevel}
          </div>
          
          {/* Target HP bar */}
          <div
            style={{
              height: 6,
              background: '#0d1117',
              border: '1px solid #30363d',
              marginBottom: '2px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${targetMaxHp > 0 ? (targetHp / targetMaxHp) * 100 : 0}%`,
                background: targetColor,
                transition: 'width 100ms linear',
              }}
            />
          </div>
          <div style={{ color: '#888', fontSize: '8px' }}>
            HP: {Math.floor(targetHp)}/{Math.floor(targetMaxHp)}
          </div>
        </div>
      </div>

      {/* Player Status Bar */}
      <div style={{ padding: '8px 12px', background: '#0d1117', borderBottom: '1px solid #21262d' }}>
        <div style={{ display: 'flex', gap: '12px', fontSize: '9px' }}>
          {/* Player HP */}
          <div style={{ flex: 1 }}>
            <div style={{ color: '#2ecc71', fontWeight: 700, marginBottom: '2px' }}>YOUR HP</div>
            <div
              style={{
                height: 4,
                background: '#1a1a1a',
                border: '1px solid #30363d',
                marginBottom: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${playerMaxHp > 0 ? (playerHp / playerMaxHp) * 100 : 0}%`,
                  background: playerHp / playerMaxHp > 0.5 ? '#2ecc71' : 
                              playerHp / playerMaxHp > 0.25 ? '#f39c12' : '#e74c3c',
                }}
              />
            </div>
            <div style={{ color: '#888' }}>{playerHp}/{playerMaxHp}</div>
          </div>

          {/* Player SP/Stamina */}
          <div style={{ flex: 1 }}>
            <div style={{ color: '#3498db', fontWeight: 700, marginBottom: '2px' }}>YOUR SP</div>
            <div
              style={{
                height: 4,
                background: '#1a1a1a',
                border: '1px solid #30363d',
                marginBottom: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${playerMaxSP > 0 ? (playerSP / playerMaxSP) * 100 : 0}%`,
                  background: playerSP / playerMaxSP > 0.75 ? '#3498db' : 
                              playerSP / playerMaxSP > 0.25 ? '#f39c12' : '#e74c3c',
                }}
              />
            </div>
            <div style={{ color: '#888' }}>{playerSP}/{playerMaxSP}</div>
          </div>
        </div>
      </div>

      {/* Focus area */}
      <div
        style={{
          position: 'relative',
          width: AREA_SIZE,
          height: AREA_SIZE,
          margin: '0 auto 12px',
          background: '#0d1117',
          border: '2px solid #30363d',
        }}
      >
        {/* Target */}
        <div
          style={{
            position: 'absolute',
            left: `${50 + (targetX / AREA_SIZE) * 50}%`,
            top: `${50 + ((targetY - AREA_Y_OFFSET) / AREA_SIZE) * 50}%`,
            transform: 'translate(-50%, -50%)',
            width: TARGET_SIZE,
            height: TARGET_SIZE,
            background: getTargetColorByFaction(targetFaction),
            border: `2px solid ${getTargetColorByFaction(targetFaction)}`,
            opacity: 0.7,
          }}
        />

        {/* Crosshair - thin cross with center dot */}
        {(() => {
          const dx = state.crosshairX - state.targetX;
          const dy = state.crosshairY - state.targetY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const threshold = (TARGET_SIZE + CROSSHAIR_SIZE) / (2 * AREA_SIZE);
          const isOnTarget = distance < threshold;
          const crosshairColor = isOnTarget ? '#48bb78' : '#58a6ff';

          return (
            <svg
              style={{
                position: 'absolute',
                left: `${50 + (crossX / AREA_SIZE) * 50}%`,
                top: `${50 + ((crossY - AREA_Y_OFFSET) / AREA_SIZE) * 50}%`,
                transform: 'translate(-50%, -50%)',
                width: CROSSHAIR_SIZE + 12,
                height: CROSSHAIR_SIZE + 12,
                overflow: 'visible',
              }}
              viewBox={`${-(CROSSHAIR_SIZE / 2 + 6)} ${-(CROSSHAIR_SIZE / 2 + 6)} ${CROSSHAIR_SIZE + 12} ${CROSSHAIR_SIZE + 12}`}
            >
              {/* Vertical line */}
              <line x1="0" y1={-(CROSSHAIR_SIZE / 2)} x2="0" y2="-5" stroke={crosshairColor} strokeWidth="3" />
              <line x1="0" y1="5" x2="0" y2={CROSSHAIR_SIZE / 2} stroke={crosshairColor} strokeWidth="3" />
              {/* Horizontal line */}
              <line x1={-(CROSSHAIR_SIZE / 2)} y1="0" x2="-5" y2="0" stroke={crosshairColor} strokeWidth="3" />
              <line x1="5" y1="0" x2={CROSSHAIR_SIZE / 2} y2="0" stroke={crosshairColor} strokeWidth="3" />
              {/* Center dot */}
              <rect x="-2" y="-2" width="4" height="4" fill={crosshairColor} />
            </svg>
          );
        })()}

        {/* Fires */}
        {fires.map((fire) => {
          const fireX = fire.x * AREA_SIZE;
          const fireY = fire.y * AREA_SIZE;
          const age = Date.now() - fire.spawnTime;
          const progress = Math.min(1, age / fire.lifetime);
          const lifeRatio = progress;
          const r = 255;
          const g = Math.floor(165 * (1 - lifeRatio));
          const fireColor = `rgb(${r}, ${g}, 0)`;

          return (
            <div
              key={fire.id}
              style={{
                position: 'absolute',
                left: fireX,
                top: fireY,
                transform: 'translate(-50%, -50%)',
              }}
            >
              {/* Ripple ring effect */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: FIRE_SIZE + 4,
                  height: FIRE_SIZE + 4,
                  border: '2px solid #ffd700',
                  opacity: 0.7 * (1 - (progress % 0.5) * 2),
                  animation: `ripple-expand 0.42s infinite`,
                }}
              />
              {/* Fire square */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: FIRE_SIZE,
                  height: FIRE_SIZE,
                  background: fireColor,
                  border: '2px solid #ffff00',
                  opacity: 1 - progress * 0.3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#ffffff',
                  textShadow: '0 0 3px #000000',
                }}
              >
                {fire.letter}
              </div>
              {/* Press indicator dots below letter */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, calc(-50% + 8px))',
                  display: 'flex',
                  gap: '3px',
                  justifyContent: 'center',
                }}
              >
                {Array.from({ length: FIRE_PRESSES_REQUIRED }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: i < fire.pressesRemaining ? '#ffd700' : '#666666',
                      opacity: 1 - progress * 0.3,
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div style={{ padding: '0 12px 8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 10 }}>
          <span style={{ color: '#f6e05e' }}>FOCUS</span>
          <span style={{ color: '#f6e05e' }}>x{state.bonusMultiplier.toFixed(1)}</span>
        </div>
        <div
          style={{
            height: 10,
            background: '#21262d',
            border: '2px solid #30363d',
            marginBottom: 6,
            position: 'relative',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${state.focusMeter}%`,
              background: state.focusMeter >= 80 ? '#48bb78' : state.focusMeter >= 50 ? '#f6e05e' : '#fc8181',
              transition: 'width 50ms linear',
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 10 }}>
          <span style={{ color: '#7ee787' }}>DEFENSE</span>
          <span style={{ color: '#7ee787' }}>{state.defenseModifier.toFixed(1)}x</span>
        </div>
        <div
          style={{
            height: 10,
            background: '#21262d',
            border: '2px solid #30363d',
            marginBottom: 8,
            position: 'relative',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${((state.defenseModifier - 0.5) / 1.0) * 100}%`,
              background: state.defenseModifier >= 1.2 ? '#48bb78' : state.defenseModifier >= 0.9 ? '#f6e05e' : '#fc8181',
              transition: 'width 50ms linear',
            }}
          />
        </div>
      </div>

      {/* Instructions */}
      <div style={{ padding: '0 12px 8px', fontSize: 8, color: '#8b949e', textAlign: 'center', lineHeight: 1.3 }}>
        WASD to aim | Press letter keys to suppress fires | ESC to exit
      </div>
      <div style={{ padding: '0 12px 12px', fontSize: 9, color: '#5dade2', textAlign: 'center', fontWeight: 'bold' }}>
        SPACE to engage minigame
      </div>
    </div>
  );
};
